import { Components } from '@mui/material/styles';
import { Theme, Box } from '@mui/material';
import { ArrowLeftIcon } from '@/src/components/icon/stroke/ArrowLeftIcon';
import { ArrowRightIcon } from '@/src/components/icon/stroke/ArrowRightIcon';
import { ChevronPreviousIcon } from '@/src/components/icon/stroke/ChevronPreviousIcon';
import { ChevronNextIcon } from '@/src/components/icon/stroke/ChevronNextIcon';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';

export const flowTablePagination: Components<Theme>['MuiTablePagination'] = {
  defaultProps: {
    // not recommended properties, <Pagination /> component should be utilized instead
    showFirstButton: false,
    showLastButton: false,
    // adjust label
    //  we are obtaining max page from the available params
    //    - first page, calculated from 'from' and 'to'
    //    - other pages, calculated from 'from' - 1 and diving by current page
    labelDisplayedRows: ({ from, to, count, page }) => (
      <>
        <Box component="strong">{page + 1}</Box>
        <Box component="span" className="divider">{` / `}</Box>
        <Box component="span">{Math.round(count / (page >= 1 ? (from - 1) / page : to - from + 1)) || 1}</Box>
      </>
    ),
    // correct icons
    slots: {
      actions: {
        // navigation arrows
        previousButtonIcon: ArrowLeftIcon,
        nextButtonIcon: ArrowRightIcon,
        // first & last extra arrow (hidden by default)
        firstButtonIcon: ChevronPreviousIcon,
        lastButtonIcon: ChevronNextIcon,
      },
    },
    slotProps: {
      actions: {
        previousButton: {
          size: 'medium',
          className: 'MuiTablePagination-actions-previous',
        },
        nextButton: {
          size: 'medium',
          className: 'MuiTablePagination-actions-next',
        },
        firstButton: {
          size: 'medium',
        },
        lastButton: {
          size: 'medium',
        },
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // we are outside table
      border: `1px solid ${theme.palette.surface.secondary}`,
      borderRadius: '8px',
      // part of <TableFooter /> and <TableRow />
      '&td': {
        border: 'none',
        borderRadius: '0',
      },
    }),
    // wrapper under root
    toolbar: ({ theme }) => ({
      position: 'relative',
      justifyContent: 'center',
      // mimic style from tableCell.theme
      [theme.breakpoints.up('md')]: {
        minHeight: '80px',
        // right padding also affect position of displayedRows
        padding: '4px 24px',
      },
      // responsive
      [theme.breakpoints.down('md')]: {
        // support multiple rows
        flexWrap: 'wrap',
        // adjust proportions
        minHeight: '0',
        padding: '8px 16px',
      },
    }),
    // first element on left side
    spacer: ({ theme }) => ({
      // make sure to keep most of the space on the left
      flexGrow: '1',
      flexBasis: 'auto',
      // in case text is provided
      ...theme.typography.body,
      // tiny responsive
      [theme.breakpoints.down('sm')]: {
        // take whole row
        flexBasis: '100%',
        textAlign: 'center',
      },
    }),
    // 'row per page:' text
    selectLabel: {
      margin: '0',
    },
    // input field for amount of rows
    input: ({ theme }) => ({
      margin: '0',
      padding: '0',
      borderRadius: '4px',
      ...theme.typography.body,
      ...getFocusRingStyle(theme, false, '0px', '&.Mui-focused'),
      [theme.breakpoints.up('xs')]: {
        fontWeight: '700',
      },
      '&& .MuiSelect-select': {
        padding: '4px 26px 4px 4px',
      },
    }),
    // label of current page, 1/10
    displayedRows: ({ theme, ownerState }) => ({
      // move into actions
      position: 'absolute',
      // IconButton size + padding
      right: `${(ownerState.showLastButton ? 44 * 2 : 44) + 24}px`,
      // this requires fixed size, also prevents jumping
      width: '90px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      // match size of IconButtons
      height: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // proportions and style
      margin: '0',
      color: theme.palette.mode === 'dark' ? theme.palette.onSurface.os300 : theme.palette.onSurface.os700,
      strong: {
        color: theme.palette.text.primary,
      },
      '.divider': {
        fontWeight: '400',
        margin: '0 4px',
      },
      // responsive
      [theme.breakpoints.down('md')]: {
        // move to 2nd row (matches new padding from toolbar)
        bottom: '8px',
        // move to center
        right: '0',
        left: '0',
        margin: '0 auto',
      },
    }),
    // navigation arrows, < >
    actions: ({ theme }) => ({
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      // make sure to keep most of the space on the right
      flexGrow: '1',
      '& .MuiTablePagination-actions-next': {
        // extra space for displayedRows
        marginLeft: '90px',
      },
      // responsive
      [theme.breakpoints.down('md')]: {
        // move to new row
        flexBasis: '100%',
        // center content
        justifyContent: 'center',
      },
    }),
  },
};
